import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';
import useToken from '../../hooks/useToken';
import { useNavigate } from 'react-router-dom';
import useProgressRedirect from '../../hooks/useProgressRedirect';
import ProgressTracker from '../shared/ProgressTracker';
import { Timer } from '../shared/Timer';
import ScopeFocusInstructions from '../shared/ScopeFocusInstructions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSitemap, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';

const COMPLETE_KEY = cat => `dr_complete_${cat}`;

function DocumentationReview() {
  const { token, refreshAccessToken, getValidToken } = useToken();
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [elapsed,      setElapsed]      = useState(null);
  const [limitSecs,    setLimitSecs]    = useState(600);
  const [overLimit,    setOverLimit]    = useState(false);
  const [drStatus,     setDrStatus]     = useState(null);
  const [proceedMsg,   setProceedMsg]   = useState('');
  const [proceeding,   setProceeding]   = useState(false);
  const [instructions, setInstructions] = useState(null);

  const websiteId           = useRef(null);
  const threatModelProvided = useRef(null);
  const hasProceeded        = useRef(false);

  const navigate = useNavigate();
  useProgressRedirect('DOCUMENTATION_REVIEW');

  const parseServerDate = s => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  const fetchDrStatus = useCallback(async () => {
    try {
      const auth = await getValidToken();
      const res  = await fetch('/api/dr/status', { headers: { Authorization: `Bearer ${auth}` } });
      if (res.ok) setDrStatus(await res.json());
    } catch (_) {}
  }, [token, refreshAccessToken]);

  // Fetch assignment + start timer
  useEffect(() => {
    if (elapsed !== null && websiteId.current !== null) return;
    setLoading(true);
    (async () => {
      try {
        const auth = await getValidToken();

        const assignRes  = await fetch('/api/assignment', { headers: { Authorization: `Bearer ${auth}` } });
        const assignments = await assignRes.json();
        const assigned   = assignments.find(w => w.order_index === 1);
        if (!assigned) { setError('No website assignment found'); return; }

        websiteId.current           = assigned.website_id;
        threatModelProvided.current = !!assigned.threat_model_provided;

        const instrRes = await fetch(`/api/website/${assigned.website_id}/testing-instructions`, {
          headers: { Authorization: `Bearer ${auth}` },
        });
        if (instrRes.ok) setInstructions(await instrRes.json());

        let limitMins = assigned.time_limit_document_minutes;
        if (!limitMins) limitMins = threatModelProvided.current ? 15 : 10;
        setLimitSecs(limitMins * 60);

        let serverStarted = parseServerDate(assigned.document_review_started_at);
        if (!serverStarted) {
          const startRes = await fetch(`/api/website/${assigned.website_id}/document-review/start`, {
            method: 'POST', headers: { Authorization: `Bearer ${auth}` },
          });
          const data = await startRes.json();
          serverStarted = parseServerDate(data?.assignment?.document_review_started_at) || new Date();
        }

        const diff = Math.floor((Date.now() - serverStarted.getTime()) / 1000);
        setElapsed(Math.max(0, diff));
        if (diff >= limitMins * 60) setOverLimit(true);
      } catch (err) {
        console.error(err);
        setError('Failed to load documentation or assignment');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, refreshAccessToken, elapsed]);

  // Fetch DR status on mount and listen for localStorage updates from viewer tabs
  useEffect(() => {
    fetchDrStatus();
    const onStorage = e => {
      if (e.key?.startsWith('dr_complete_')) fetchDrStatus();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchDrStatus]);

  const openDocViewer = () => {
    window.open(`/document-view/${websiteId.current}?quiz=1`, '_blank');
  };

  const openThreatModelViewer = () => {
    window.open(`/threatmodelviewer?quiz=1`, '_blank');
  };

  const handleProceed = async () => {
    if (hasProceeded.current) return;
    hasProceeded.current = true;
    setProceeding(true);
    try {
      const auth = await getValidToken();

      await fetch(`/api/website/${websiteId.current}/document-review/end`, {
        method: 'POST', headers: { Authorization: `Bearer ${auth}` },
      });

      const res = await fetch('/api/player/progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
        body: JSON.stringify({ progress_step: 'WEBSITE1' }),
      });

      if (res.ok) {
        setProceedMsg('Responses recorded. Pentest begins now.');
        setTimeout(() => navigate('/website1', { replace: true }), 1500);
      } else {
        const d = await res.json();
        setError(d.message || 'Failed to advance');
        setProceeding(false);
        hasProceeded.current = false;
      }
    } catch (err) {
      console.error(err);
      setError('Progress update failed');
      setProceeding(false);
      hasProceeded.current = false;
    }
  };

  const dd = drStatus?.design_doc;
  const tm = drStatus?.threat_model;
  const ddComplete = dd?.all_correct;
  const tmComplete = !tm?.applicable || tm?.all_correct;
  const canProceed = ddComplete && tmComplete;

  if (error)   return <Container className="py-5"><Alert variant="danger">{error}</Alert></Container>;
  if (elapsed === null) return <Container className="py-5"><Spinner animation="border" /></Container>;

  return (
    <Container className="py-5">
      <ProgressTracker currentStep="documentation_review" />
      <Card className="shadow p-4">
        <h2 className="mb-2">Documentation Review</h2>
        <p className="text-muted mb-3">
          Open each viewer below to review the documents and answer the questions inside.
          Come back here once all questions are answered correctly to proceed.
        </p>

        {instructions && (
          <ScopeFocusInstructions instructions={instructions} defaultOpen />
        )}

        <Timer
          initialElapsed={elapsed}
          limit={limitSecs}
          onLimitReached={() => setOverLimit(true)}
        />
        {overLimit && (
          <div className="mb-3 p-3 rounded" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
            <p className="mb-0 fw-bold" style={{ color: '#856404', fontSize: '1rem' }}>
              ⏰ Suggested review time has passed. Feel free to proceed whenever you are ready.
            </p>
          </div>
        )}

        {/* Viewer status cards */}
        <div className="row g-3 mb-4">
          {/* Design Document card */}
          <div className={tm?.applicable ? 'col-md-6' : 'col-12'}>
            <div className="card h-100" style={{
              border: `2px solid ${ddComplete ? '#10b981' : '#6366f1'}`,
              borderRadius: 10,
            }}>
              <div className="card-body d-flex flex-column gap-2">
                <h6 className="mb-1">📄 Design Document Questions</h6>
                <StatusBadge status={dd} />
                <Button
                  variant="primary"
                  className="mt-auto"
                  disabled={!websiteId.current}
                  onClick={openDocViewer}
                >
                  <FontAwesomeIcon icon={faSitemap} className="me-2" />
                  Open Design Document Viewer
                  <span style={{ display: 'block', fontSize: '0.72em', opacity: 0.85 }}>↗ opens in a new tab</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Threat Model card — Group B only */}
          {tm?.applicable && (
            <div className="col-md-6">
              <div className="card h-100" style={{
                border: `2px solid ${tm?.all_correct ? '#10b981' : '#6366f1'}`,
                borderRadius: 10,
              }}>
                <div className="card-body d-flex flex-column gap-2">
                  <h6 className="mb-1">🔐 Threat Model Questions</h6>
                  <StatusBadge status={tm} />
                  <Button
                    variant="success"
                    className="mt-auto"
                    onClick={openThreatModelViewer}
                  >
                    <FontAwesomeIcon icon={faProjectDiagram} className="me-2" />
                    Open Threat Model Viewer
                    <span style={{ display: 'block', fontSize: '0.72em', opacity: 0.85 }}>↗ opens in a new tab</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {proceedMsg ? (
          <Alert variant="info">{proceedMsg}</Alert>
        ) : (
          <div className="d-flex align-items-center gap-3">
            <Button
              variant="primary"
              size="lg"
              onClick={handleProceed}
              disabled={!canProceed || proceeding}
            >
              {proceeding ? (
                <><Spinner animation="border" size="sm" className="me-2" />Saving…</>
              ) : (
                'Proceed to Experiment →'
              )}
            </Button>
            {!canProceed && (
              <span className="text-muted small">
                Answer all questions correctly in the viewer(s) to proceed.
              </span>
            )}
          </div>
        )}
      </Card>
    </Container>
  );
}

function StatusBadge({ status }) {
  if (!status) {
    return <span className="text-muted small">Loading…</span>;
  }
  if (!status.applicable) {
    return <span className="text-muted small">Not applicable</span>;
  }
  if (status.all_correct) {
    return (
      <span style={{ color: '#065f46', fontWeight: 600, fontSize: 13 }}>
        ✅ All {status.total} questions correct
      </span>
    );
  }
  if (status.correct_count === 0) {
    return <span className="text-muted small">Not yet started — open the viewer to answer questions</span>;
  }
  return (
    <span style={{ color: '#92400e', fontWeight: 600, fontSize: 13 }}>
      ⏳ {status.correct_count} / {status.total} correct — keep going!
    </span>
  );
}

export default DocumentationReview;
